import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, tap, throwError } from "rxjs";
import { IResponse } from "../core/interfaces/IResponse";
import { HttpClient } from "@angular/common/http";
import { MESSAGE } from "../core/constantes";
import { IPaginate } from "../core/interfaces/ipaginate";
import { BaseService } from "./base.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class ComplaintService extends BaseService<any> {
  private _complaints: BehaviorSubject<any | null>;

  constructor(private http: HttpClient) {
    super(http, "admin/complaints");
    this._complaints = new BehaviorSubject(null);
  }

  override getAll(
    page: number = 1,
    limit: number = 10,
    search: string = "",
    order: string = "desc"
  ): Observable<IResponse<IPaginate<any>>> {
    return super.getAll(page, limit, search, order).pipe(
      catchError((err, caught) => {
        return throwError(err);
      })
    );
  }

  getAllWithFilter(
    category: string = "",
    status: string = "",
    startDateRange: string = "",
    endDateRange: string = "",
    page: number = 1,
    size: number = 10,
    search: string = "",
    order: string = "desc",
    orderBy: string = "createdAt",
    priorite: string = "",
    assignedAdminId: string = "",
    slaState: string = "",
    institutionId: string = ""
  ): Observable<IResponse<IPaginate<any>>> {
    // L'endpoint backend `admin/complaints/filter` applique :
    // - le filtre de sensibilité (admin.sensibilityLevel >= complaint.sensitivityLevel)
    // - tous les filtres + le tri (whitelist d'orderBy)
    // - le filtre opt-in `?institutionId=` validé par resolveOptInTenantFilter
    //   (chantier R Sprint 2.4 — réservé SystemAdmin, 403 sinon)
    // → rien n'est filtré/trié côté client (cf. memo "filtering must be backend-driven")
    let paramObj: any = {
      page,
      size,
      q: search,
      orderBy,
      order, // alias backend : `order` (et non plus `orderType`)
    };
    if (category) paramObj.category = category;
    if (status) paramObj.status = status;
    if (endDateRange) paramObj.endDateRange = endDateRange;
    if (startDateRange) paramObj.startDateRange = startDateRange;
    if (priorite) paramObj.priorite = priorite;
    if (assignedAdminId) paramObj.assignedAdminId = assignedAdminId;
    if (slaState) paramObj.slaState = slaState;
    if (institutionId) paramObj.institutionId = institutionId;

    return this.http.get<IResponse<IPaginate<any>>>(
      `${environment.baseUrl}admin/complaints/filter`,
      {
        params: paramObj,
      }
    );
  }

  override getOne(id: string): Observable<IResponse<any>> {
    return super.getOne(id).pipe(
      tap((response) => {
        this._complaints.next(response.data);
      })
    );
  }

  get complaint$(): Observable<any> {
    return this._complaints.asObservable();
  }

  addComment(elementId: string, data: any): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/comment`,
      data
    );
  }

  getHistory(elementId: string): Observable<IResponse<any>> {
    return this.http.get<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/history/${elementId}`
    );
  }

  assignFaculty(data: string, elementId: string): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/faculty`,
      data
    );
  }

  assignOrganisation(
    data: string,
    elementId: string
  ): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/organization`,
      data
    );
  }

  addFiles(elementId: string, data: any): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/join-files`,
      data
    );
  }

  changeStatus(elementId: string, data: any): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/status`,
      data
    );
  }

  changeCategory(elementId: string, data: any): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/category`,
      data
    );
  }

  linkToStructure(elementId: string, data: any): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${elementId}/university-structure`,
      data
    );
  }

  getUFRS() {
    return this.http.get<IResponse<any>>(
      `${environment.baseUrl}guest/complaint/universities-drop-down`
    );
  }

  search(query: string): Observable<IResponse<any>> {
    return this.http.post<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/search`,
      { trackingCodes: [query] }
    );
  }

  removeBulk(ids: string[]): Observable<IResponse<any>> {
    return this.http.delete<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/remove-bulk`,
      { body: ids }
    );
  }

  /** Phase 2 — bulk status. Backend retourne `{ success: number, failed: string[] }`. */
  bulkChangeStatus(
    ids: string[],
    status: string,
    comment?: string
  ): Observable<IResponse<{ success: number; failed: string[] }>> {
    return this.http.put<IResponse<{ success: number; failed: string[] }>>(
      `${environment.baseUrl}admin/complaints/bulk-status`,
      { ids, status, comment: comment ?? "" }
    );
  }

  /** Phase 2 — bulk assign. `adminId` null/vide ⇒ désassigner. */
  bulkAssign(
    ids: string[],
    adminId: string | null
  ): Observable<IResponse<{ success: number; failed: string[] }>> {
    return this.http.put<IResponse<{ success: number; failed: string[] }>>(
      `${environment.baseUrl}admin/complaints/bulk-assign`,
      { ids, adminId: adminId ?? null }
    );
  }

  /** Phase 2.5 — édition unitaire de la priorité d'une plainte. */
  changePriority(
    id: string,
    priorite: "URGENTE" | "HAUTE" | "NORMALE"
  ): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${id}/priority`,
      { priorite }
    );
  }

  restore(id: string): Observable<IResponse<any>> {
    return this.http.put<IResponse<any>>(
      `${environment.baseUrl}admin/complaints/${id}/restore`,
      {}
    );
  }

  getTrash(
    page: number = 1,
    limit: number = 10,
    search: string = "",
    order: string = "desc",
    startDateRange: string = "",
    endDateRange: string = "",
    orderBy: string = "createdAt"
  ): Observable<IResponse<IPaginate<any>>> {
    let paramObj: any = {
      page,
      limit,
      filter: search,
      orderBy,
      orderType: order,
    };

    if (startDateRange) {
      paramObj.startDateRange = startDateRange;
    }

    if (endDateRange) {
      paramObj.endDateRange = endDateRange;
    }

    return this.http.get<IResponse<IPaginate<any>>>(
      `${environment.baseUrl}admin/complaints/trash/all`,
      { params: paramObj }
    );
  }
}
